import { Alert, Box, Button, CircularProgress, FormControl, MenuItem, Paper, Select, SelectChangeEvent, Skeleton, Typography } from "@mui/material";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Program } from "../../../api/interfaces/Program";
import { useCallback, useEffect, useState } from "react";
import { handleApiError } from "../../../utils/errorHandler";
import { pushNotification } from "../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';
import { NO_DATA_TO_DISPLAY_URL } from "../../../utils/constants";
import { getVideos, reorderVideos } from "../../../api/services/video";
import { Subject } from "../../../api/interfaces/Subject";
import { Video } from "../../../api/interfaces/Video";
import { getSubjects } from "../../../api/services/subjects";

interface RearrangeVideosDrawerContentProps {
    programs: Program[];
    firstProgramId: string;
    firstProgramTitle: string;
    closeToggle: () => void;
    onVideosCallback?: (programId: string) => void;
}

const SortableItem = ({ id, title }: { id: string; title: string }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : undefined,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Paper
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            style={style}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                mb: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                backgroundColor: '#fff',
                boxShadow: isDragging ? 3 : 0,
                minHeight: 48,
                cursor: 'grab',
            }}
        >
            {/* Drag Icon as visual cue only */}
            <DragIndicatorIcon fontSize="small" sx={{ color: '#999' }} />

            {/* Title with ellipsis */}
            <Typography
                variant="body2"
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flexGrow: 1,
                }}
            >
                {title}
            </Typography>
        </Paper>
    );
};

const RearrangeVideosDrawerContent: React.FC<RearrangeVideosDrawerContentProps> = ({
    programs,
    firstProgramId,
    firstProgramTitle,
    closeToggle,
    onVideosCallback
}) => {
    const [selectedProgramId, setSelectedProgramId] = useState<string>('');
    const [selectedProgramTitle, setSelectedProgramTitle] = useState<string>('');
    const [selectedSubjectId, setSelectedSubjectId] = useState<string>('');
    const [isApplyChangesLoading, setIsApplyChangesLoading] = useState<boolean>(false);
    const [isLoadingDragList, setIsLoadingDragList] = useState<boolean>(false);
    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [videos, setVideos] = useState<Video[]>([]);

    const fetchSubjects = useCallback(async (programId: string) => {
        try {
            const subjectsResponse = await getSubjects({ programId });
            setSubjects(subjectsResponse.data.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchVideos = useCallback(async (subjectId: string) => {
        try {
            const videosResponse = await getVideos({ programId: selectedProgramId, subjectId });
            setVideos(videosResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    // Initial fetch
    const fetchInitialProgramReferences = useCallback(async () => {
        try {
            setIsLoadingDragList(true);
            setSelectedProgramId(firstProgramId);
            setSelectedProgramTitle(firstProgramTitle);

            // Retreive the first record right ahead
            await fetchSubjects(firstProgramId);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsLoadingDragList(false);
        } finally {
            setIsLoadingDragList(false);
        }
    }, [fetchSubjects]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        })
    );

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = videos.findIndex((item) => item._id === active.id);
        const newIndex = videos.findIndex((item) => item._id === over.id);
        setVideos((prev) => arrayMove(prev, oldIndex, newIndex));
    };

    const handleSelectedProgram = async (event: SelectChangeEvent<string>) => {
        setIsLoadingDragList(true);
        const programId = event.target.value;
        setSelectedProgramId(programId);

        // Find the selected program from the list
        const selectedProgram = programs.find(p => p._id === programId);
        if (selectedProgram) {
            setSelectedProgramTitle(selectedProgram.name);
        }

        try {
            await fetchSubjects(programId);
            setIsApplyChangesLoading(false);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsLoadingDragList(false);
        } finally {
            setIsLoadingDragList(false);
        }
    }

    const handleSelectedSubjects = async (event: SelectChangeEvent<string>) => {
        setIsLoadingDragList(true);
        const subjectId = event.target.value;
        setSelectedSubjectId(subjectId);

        try {
            await fetchVideos(subjectId);
            setIsApplyChangesLoading(false);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsLoadingDragList(false);
        } finally {
            setIsLoadingDragList(false);
        }
    }

    const handleSubmitRearrange = async () => {
        setIsApplyChangesLoading(true);
        const orderedVideoIds = videos.map(video => video._id);

        try {
            const response = await reorderVideos({
                subjectId: selectedSubjectId,
                videos: orderedVideoIds
            });

            if (selectedProgramId === firstProgramId &&
                selectedSubjectId === selectedSubjectId &&
                onVideosCallback
            ) {
                onVideosCallback(selectedProgramId);
            }

            pushNotification({
                details: response.message,
                type: NotificationTypes.Success,
            });
            setIsApplyChangesLoading(false);
            closeToggle();
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsApplyChangesLoading(false);
        } finally {
            setIsApplyChangesLoading(false);
        }

    }

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            setSelectedProgramId(firstProgramId);
            setIsApplyChangesLoading(true);
            try {
                await fetchInitialProgramReferences();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setIsApplyChangesLoading(false);
            }
        };
        if (firstProgramId) {
            run();
        }
    }, [fetchInitialProgramReferences]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Top content and scrollable drag list */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
                <Box sx={{ pb: 1 }}>
                    <Alert severity="info" icon={false} sx={{ backgroundColor: '#E5F8FE', color: '#26C6F9' }}>
                        <Typography variant="subtitle2">
                            Select a program first, then select a subject, and apply the new arrangement to the video list.
                        </Typography>
                    </Alert>
                </Box>

                <FormControl>
                    <Select
                        value={selectedProgramId}
                        onChange={handleSelectedProgram}
                        defaultValue={selectedProgramId}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Program' }}
                        disabled={isApplyChangesLoading}
                        fullWidth
                        sx={{
                            color: '#818181',
                            '.MuiSelect-icon': {
                                color: '#818181',
                            },
                        }}
                        MenuProps={{
                            disableScrollLock: true,
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                },
                            },
                        }}
                    >
                        {programs.map((program) => (
                            <MenuItem key={program._id} value={program._id}>
                                <Typography
                                    noWrap
                                    sx={{
                                        maxWidth: '350px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: 'block',
                                    }}
                                >
                                    {program.name}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl>
                    <Select
                        value={selectedSubjectId}
                        onChange={handleSelectedSubjects}
                        defaultValue={selectedSubjectId}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Subject' }}
                        disabled={isApplyChangesLoading}
                        fullWidth
                        sx={{
                            color: '#818181',
                            '.MuiSelect-icon': {
                                color: '#818181',
                            },
                        }}
                        MenuProps={{
                            disableScrollLock: true,
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                },
                            },
                        }}
                    >
                        {subjects.map((subject) => (
                            <MenuItem key={subject._id} value={subject._id}>
                                <Typography
                                    noWrap
                                    sx={{
                                        maxWidth: '350px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: 'block',
                                    }}
                                >
                                    {subject.name}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    List of Videos {selectedProgramTitle && `under ${selectedProgramTitle}`}
                </Typography>

                {/* Scrollable Drag and Drop List */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1, overflowX: 'hidden' }}>
                    {isLoadingDragList ? (
                        Array.from({ length: 4 }).map((_, index) => (
                            <Box key={index} sx={{ mb: 1 }}>
                                <Skeleton variant="rectangular" height={48} animation="wave" />
                            </Box>
                        ))
                    ) : videos.length === 0 ? (
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                color: '#818181'
                            }}
                        >
                            <Box component="img" src={NO_DATA_TO_DISPLAY_URL} alt="No data" sx={{ width: '300px', mb: 1 }} />
                        </Box>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={videos.map((video) => video._id)}
                                strategy={verticalListSortingStrategy}
                                disabled={isApplyChangesLoading || isLoadingDragList}
                            >
                                {videos.map((video) => (
                                    <SortableItem key={video._id} id={video._id} title={video.title} />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}
                </Box>
            </Box>

            <Box sx={{ pt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitRearrange}
                    disabled={isApplyChangesLoading}
                    fullWidth
                    startIcon={isApplyChangesLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    APPLY CHANGES
                </Button>
            </Box>
        </Box>
    );
};

export default RearrangeVideosDrawerContent;